import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import DataTable, { Column } from '../../../components/DataTable/DataTable';
import { fetcher } from '../../../services/fetcher';
import { useTheme } from '../../../contexts/ThemeContext';
import {
  Badge,
  Button,
  Card,
  DocumentTextIcon,
  FolderIcon,
  SearchIcon,
  Switch,
  TextInput,
} from '../../../components/ui';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import { cn } from '../../../lib/utils';
import { ACCOUNTING_TABLE_DEFAULTS } from '../shared/typography';
import { useAccCompanies } from '../companies/useData';
import { useAccPeriods } from '../periods/useData';
import { ReportExportButtons } from '../../../components/reports/ReportExportButtons';
import { useReportExport } from './shared/useReportExport';
import { PeriodDateRangeFields } from './shared/reportFields';
import { useDefaultAccountingPeriod } from './shared/useDefaultAccountingPeriod';

type TrialBalanceRow = {
  accountId: number;
  parentId: number | null;
  levelCode: string;
  levelName: string;
  isPosting: boolean;
  depth: number;
  previousDebit: number;
  previousCredit: number;
  previousBalance: number;
  periodDebit: number;
  periodCredit: number;
  periodBalance: number;
  totalDebit: number;
  totalCredit: number;
  totalBalance: number;
};

type TrialBalanceResponse = {
  rows: TrialBalanceRow[];
  totals: Omit<TrialBalanceRow, 'accountId' | 'levelCode' | 'levelName'>;
  recordCount: number;
};

const formatAmount = (value: number) =>
  value === 0 ? '' : value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const defaultPeriodDates = () => {
  const now = new Date();
  const from = `${now.getFullYear()}-01-01`;
  const to = now.toISOString().slice(0, 10);
  return { from, to };
};


const amountColumn = (
  key: keyof TrialBalanceRow,
  header: string,
  groupId: string,
  groupHeader: string,
  totalValue?: number,
): Column<TrialBalanceRow> => ({
  key,
  header,
  align: 'right',
  width: '7.5rem',
  sortable: true,
  columnGroup: { id: groupId, header: groupHeader },
  footerType: 'custom',
  footerRender: () => (totalValue == null ? null : formatAmount(totalValue)),
  render: (value: number) => formatAmount(value),
});

export const TrialBalance: React.FC = () => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const defaults = useMemo(() => defaultPeriodDates(), []);

  const { data: companiesRaw, isLoading: companiesLoading } = useAccCompanies('', 1, 500, {
    filters: { filters: { isActive: ['true'] } },
  });
  const companies = useMemo(
    () => [...(companiesRaw ?? [])].sort((a, b) => a.code.localeCompare(b.code)),
    [companiesRaw],
  );

  const { data: periodsRaw } = useAccPeriods('', 1, 100);
  const periods = periodsRaw ?? [];

  const [selectedCompanyIds, setSelectedCompanyIds] = useState<number[]>([]);
  const [allCompanies, setAllCompanies] = useState(false);
  const [companyFilter, setCompanyFilter] = useState('');
  const [periodId, setPeriodId] = useState('');
  const [dateFrom, setDateFrom] = useState(defaults.from);
  const [dateTo, setDateTo] = useState(defaults.to);
  const [includeZeroValues, setIncludeZeroValues] = useState(false);
  const [run, setRun] = useState(false);

  useEffect(() => {
    setSelectedCompanyIds([]);
    setAllCompanies(false);
    setCompanyFilter('');
    setPeriodId('');
    setDateFrom(defaults.from);
    setDateTo(defaults.to);
    setRun(false);
  }, [defaults.from, defaults.to]);

  useEffect(() => {
    if (!periodId) return;
    const period = periods.find((p) => String(p.id) === periodId);
    if (!period) return;
    setDateFrom(period.startDate.slice(0, 10));
    setDateTo(period.endDate.slice(0, 10));
    setRun(false);
  }, [periodId, periods]);

  useDefaultAccountingPeriod(periods, (period) => {
    setPeriodId(String(period.id));
    setDateFrom(period.startDate.slice(0, 10));
    setDateTo(period.endDate.slice(0, 10));
  });

  const filteredCompanies = useMemo(() => {
    const q = companyFilter.trim().toLowerCase();
    if (!q) return companies;
    return companies.filter(
      (c) => c.code.toLowerCase().includes(q) || c.name.toLowerCase().includes(q),
    );
  }, [companies, companyFilter]);

  const toggleCompany = useCallback((id: number) => {
    setAllCompanies(false);
    setSelectedCompanyIds((prev) =>
      prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id],
    );
    setRun(false);
  }, []);

  const toggleAllCompanies = useCallback((checked: boolean) => {
    setAllCompanies(checked);
    setSelectedCompanyIds([]);
    setRun(false);
  }, []);

  const reportUrl = useMemo(() => {
    const canRun = run && dateFrom && dateTo && (allCompanies || selectedCompanyIds.length > 0);
    if (!canRun) return null;

    const params = new URLSearchParams({
      includeZeroValues: includeZeroValues ? 'true' : 'false',
      useLocalCurrency: 'true',
      dateFrom,
      dateTo,
    });
    if (!allCompanies && selectedCompanyIds.length > 0) {
      params.set('companyIds', selectedCompanyIds.join(','));
    }
    return `/accounting/reports/trial-balance?${params.toString()}`;
  }, [
    run,
    allCompanies,
    selectedCompanyIds,
    includeZeroValues,
    dateFrom,
    dateTo,
  ]);

  const { data: report, isLoading: reportLoading } = useSWR<TrialBalanceResponse>(reportUrl, fetcher);
  const rows = report?.rows ?? [];

  const periodOptions = useMemo(
    () => [
      { value: '', label: 'Custom period' },
      ...periods.map((p) => ({
        value: String(p.id),
        label: `${p.year} (${formatDateGlobal(p.startDate)} – ${formatDateGlobal(p.endDate)})`,
      })),
    ],
    [periods],
  );

  const columns = useMemo<Column<TrialBalanceRow>[]>(() => {
    const totals = report?.totals;
    return [
      {
        key: 'levelCode',
        header: 'Level Code',
        width: '11rem',
        sortable: true,
        footerType: 'custom',
        footerLabel: 'Records',
        footerRender: () => String(report?.recordCount ?? rows.length),
        render: (value: string, row) => (
          <span
            className={cn(
              'inline-flex items-center gap-1.5 min-w-0',
              !row.isPosting && 'font-semibold',
            )}
            style={{ paddingLeft: Math.min(row.depth, 6) * 12 }}
          >
            {row.isPosting ? (
              <span className={cn('shrink-0', isDark ? 'text-sky-400' : 'text-sky-600')} title="Ledger">
                <DocumentTextIcon className="h-3.5 w-3.5" aria-hidden />
              </span>
            ) : (
              <span className={cn('shrink-0', isDark ? 'text-violet-400' : 'text-violet-500')} title="Header">
                <FolderIcon className="h-3.5 w-3.5" aria-hidden />
              </span>
            )}
            <span className="tabular-nums truncate" title={value}>{value}</span>
          </span>
        ),
      },
      {
        key: 'levelName',
        header: 'Level Name',
        width: '16rem',
        sortable: true,
        footerType: 'custom',
        footerRender: () => null,
        render: (value: string, row) => (
          <span
            className={cn('truncate block', !row.isPosting && 'font-semibold')}
            title={value}
          >
            {value}
          </span>
        ),
      },
      amountColumn('previousDebit', 'Pr.Debit', 'previous', 'Previous', totals?.previousDebit),
      amountColumn('previousCredit', 'Pr.Credit', 'previous', 'Previous', totals?.previousCredit),
      amountColumn('previousBalance', 'Pr. Blnc', 'previous', 'Previous', totals?.previousBalance),
      amountColumn('periodDebit', 'Period Debit', 'period', 'Period', totals?.periodDebit),
      amountColumn('periodCredit', 'Period Credit', 'period', 'Period', totals?.periodCredit),
      amountColumn('periodBalance', 'Period Blnc', 'period', 'Period', totals?.periodBalance),
      amountColumn('totalDebit', 'Total Debit', 'total', 'Total', totals?.totalDebit),
      amountColumn('totalCredit', 'Total Credit', 'total', 'Total', totals?.totalCredit),
      amountColumn('totalBalance', 'Total Blnc', 'total', 'Total', totals?.totalBalance),
    ];
  }, [isDark, report?.recordCount, report?.totals, rows.length]);

  const canSearch = Boolean(dateFrom && dateTo && (allCompanies || selectedCompanyIds.length > 0));

  const { onExportExcel, onExportPDF, exportDisabled } = useReportExport(
    rows,
    columns,
    'Trial Balance',
    'trial-balance',
    dateTo,
  );

  return (
    <div className="flex flex-col h-full min-h-0 p-1 gap-1">
      {/* Toolbar */}
      <Card className="p-4 shrink-0">
        <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
          <PeriodDateRangeFields
            periodId={periodId}
            periodOptions={periodOptions}
            onPeriodChange={(value) => {
              setPeriodId(value);
              setRun(false);
            }}
            dateFrom={dateFrom}
            dateTo={dateTo}
            onDateFromChange={(value) => {
              setPeriodId('');
              setDateFrom(value);
              setRun(false);
            }}
            onDateToChange={(value) => {
              setPeriodId('');
              setDateTo(value);
              setRun(false);
            }}
            periodClassName="w-[15rem] shrink-0"
          />

          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 ml-auto shrink-0 pb-0.5">
            <Switch
              label="Include zero values"
              checked={includeZeroValues}
              onChange={(e) => {
                setIncludeZeroValues(e.target.checked);
                setRun(false);
              }}
              size="sm"
            />
            <Button
              variant="default"
              onClick={() => setRun(true)}
              disabled={!canSearch}
              className="h-9 px-4 gap-2"
            >
              <SearchIcon className="h-4 w-4" />
              Search
            </Button>
          </div>
        </div>
      </Card>

      {/* Main workspace */}
      <div
        className={cn(
          'flex flex-1 min-h-0 overflow-hidden rounded-xl border shadow-sm',
          colors.surface,
          colors.border,
        )}
      >
        {/* Companies */}
        <aside className={cn('w-64 shrink-0 border-r flex flex-col min-h-0', colors.border, colors.surfaceHover)}>
          <div className={cn('px-3 pt-3 pb-2 space-y-2.5 border-b', colors.border, colors.surface)}>
            <div className="flex items-center justify-between gap-2">
              <h2 className={cn('text-xs font-semibold uppercase tracking-wide', colors.textMuted)}>
                Companies
              </h2>
              <span className={cn('text-[11px] tabular-nums', colors.textMuted)}>
                {allCompanies
                  ? `${companies.length} all`
                  : `${selectedCompanyIds.length}/${companies.length}`}
              </span>
            </div>

            <div
              className={cn(
                'flex items-center justify-between gap-2 rounded-lg border px-2.5 py-1.5',
                colors.border,
                allCompanies ? cn(colors.info, 'ring-1', colors.primaryBorderAll) : colors.surface,
              )}
            >
              <Switch
                label="All companies"
                checked={allCompanies}
                onChange={(e) => toggleAllCompanies(e.target.checked)}
                size="sm"
              />
            </div>

            <TextInput
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
              placeholder="Filter by code or name…"
              prefix={<SearchIcon className="h-4 w-4" />}
              showClearButton={Boolean(companyFilter)}
              size="sm"
            />
          </div>

          <div className={cn('dashboard-scrollbar flex-1 overflow-y-auto py-1', colors.surface)}>
            {companiesLoading && (
              <p className={cn('px-3 py-4 text-sm', colors.textMuted)}>Loading companies…</p>
            )}
            {!companiesLoading && companies.length === 0 && (
              <p className={cn('px-3 py-4 text-sm', colors.textMuted)}>No active companies</p>
            )}
            {filteredCompanies.map((company) => {
              const active = !allCompanies && selectedCompanyIds.includes(company.id);
              const checked = allCompanies || active;
              const label = `${company.name}`;
              return (
                <label
                  key={company.id}
                  title={label}
                  className={cn(
                    'group mx-1.5 flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 transition-colors',
                    active && cn(colors.info, 'ring-1', colors.primaryBorderAll),
                    !active && checked && colors.surfaceHover,
                    !checked && colors.interactiveHover,
                  )}
                >
                  <input
                    type="checkbox"
                    className={cn(
                      'h-3.5 w-3.5 shrink-0 rounded focus:ring-indigo-500',
                      isDark
                        ? 'border-white/20 bg-[#121a2b] text-[#6b8fb8]'
                        : 'border-slate-300 text-indigo-600',
                    )}
                    checked={checked}
                    onChange={() => toggleCompany(company.id)}
                    aria-label={label}
                  />
                  <span
                    className={cn(
                      'min-w-[2.25rem] shrink-0 text-sm tabular-nums',
                      active ? colors.primaryFg : colors.text,
                    )}
                  >
                    {company.code}
                  </span>
                  <span className={cn('min-w-0 flex-1 truncate text-sm', colors.text)}>
                    {company.name}
                  </span>
                </label>
              );
            })}
            {!companiesLoading && filteredCompanies.length === 0 && companies.length > 0 && (
              <p className={cn('px-3 py-8 text-sm text-center', colors.textMuted)}>No companies match.</p>
            )}
          </div>
        </aside>

        {/* Report grid */}
        <div className="flex-1 min-w-0 flex flex-col min-h-0">
          <div className={cn('flex-shrink-0 flex items-center justify-between gap-3 px-4 py-2.5 border-b', colors.border, colors.surfaceHover)}>
            <div className="flex flex-wrap items-center gap-2 min-w-0">
              {run && dateFrom && dateTo && (
                <Badge variant="info" className="font-normal">
                  {formatDateGlobal(dateFrom)} – {formatDateGlobal(dateTo)}
                </Badge>
              )}
              <span className={cn('text-sm', colors.textMuted)}>
                {run && report
                  ? `${report.recordCount} record${report.recordCount === 1 ? '' : 's'}`
                  : 'Choose period and companies, then search'}
              </span>
            </div>
            {run && report && (
              <div className="flex items-center gap-2 shrink-0">
                <ReportExportButtons
                  onExportExcel={onExportExcel}
                  onExportPDF={onExportPDF}
                  disabled={exportDisabled}
                />
              </div>
            )}
          </div>

          <div className="flex-1 min-h-0 overflow-auto">
            {!run && (
              <div className={cn('flex flex-col items-center justify-center h-full min-h-[220px] px-8 text-center', colors.textMuted)}>
                <div className={cn('h-12 w-12 rounded-full flex items-center justify-center mb-3', colors.surfaceHover)}>
                  <SearchIcon className="h-5 w-5 opacity-60" />
                </div>
                <p className="text-sm font-medium">Ready to search</p>
                <p className="text-xs mt-1 max-w-sm leading-relaxed">
                  Pick a period and company scope, then click Search to run the trial balance.
                </p>
              </div>
            )}
            {run && !allCompanies && selectedCompanyIds.length === 0 && (
              <div className={cn('flex items-center justify-center h-full min-h-[200px] text-sm', colors.textMuted)}>
                Select at least one company.
              </div>
            )}
            {run && (allCompanies || selectedCompanyIds.length > 0) && reportLoading && (
              <div className={cn('flex items-center justify-center h-full min-h-[200px] text-sm', colors.textMuted)}>
                Loading trial balance…
              </div>
            )}
            {run && !reportLoading && report && (allCompanies || selectedCompanyIds.length > 0) && (
              <DataTable
                {...ACCOUNTING_TABLE_DEFAULTS}
                data={rows}
                columns={columns}
                keyExtractor={(row) => String(row.accountId)}
                showFooter
                emptyMessage="No data to display."
                stickyHeader
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
